/** @jsx jsx */
import { Box } from '@bottlebooks/gatsby-design-system';
import { Link as BaseLink } from 'gatsby';
import { OutboundLink } from 'gatsby-plugin-google-analytics';
import { jsx } from 'theme-ui';

/**
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.to]
 * @param {string} [props.href]
 * @param {boolean} [props._suppressExternalLinkWarning] Don't show a warning if an external link was supplied to `to`.
 * @param {() =>void} [props.onClick]
 * @param {import('./linkVariants').LinkVariant} [props.variant]
 * @param {string} [props.fallback]
 * @param {string} [props.activeClassName] A className to set if the link is active.
 * @param {boolean} [props.openInNewTab] In case of external links, it opens a new tab. This also sets rel="noopener noreferrer" automatically.
 * @param {string} [props.type] Sets the type for a button.
 * @returns
 */
export default function Link({
  to,
  // If `to` starts with http, we convert it to an external link automatically.
  href = to && (/^https?:\/\//.test(to) || /^mailto?:/.test(to))
    ? to
    : undefined,
  _suppressExternalLinkWarning = false,
  onClick,
  variant = 'default',
  fallback = 'span',
  activeClassName,
  openInNewTab,
  ...rest
}) {
  // External links first (in case the `to` param was set to an external URL by accident).
  if (!_suppressExternalLinkWarning && to && /^https?:\/\//.test(to)) {
    console.warn(`${to} was used on an internal link. Use 'href' instead!`);
  }
  // `href` needs to be checked first because `to` might contain an external link
  // that is then converted to an href.
  if (href) {
    return (
      <Box
        as={OutboundLink}
        href={href}
        onClick={onClick}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        sx={{
          // These colors are the link defaults.
          // They can be overridden by the variant or via the sx prop on the component instance.
          color: 'primary',
          ':hover': { color: 'secondary' },
          variant: `link.${variant}`,
        }}
        {...rest}
      />
    );
  }
  if (to) {
    return (
      <BaseLink
        to={to}
        onClick={onClick}
        sx={{
          // These colors are the link defaults.
          // They can be overridden by the variant or via the sx prop on the component instance.
          color: 'primary',
          ':hover': { color: 'secondary' },
          variant: `link.${variant}`,
        }}
        activeClassName={activeClassName}
        {...rest}
      />
    );
  }
  if (onClick) {
    return (
      <Box
        as={LinkButton}
        onClick={onClick}
        sx={{
          // These colors are the button defaults.
          // They can be overridden by the variant or via the sx prop on the component instance.
          color: 'primary',
          ':hover': { color: 'secondary' },
          variant: `link.${variant}`,
        }}
        {...rest}
      />
    );
  }
  // If we don't have a `to`, `href` or `onClick` prop, we don't add colors and hover.
  return <Box as={fallback} sx={{ variant: `link.${variant}` }} {...rest} />;
}

export function LinkButton(props) {
  return (
    <button
      sx={{
        fontSize: 'inherit',
        lineHeight: 'inherit',
        fontWeight: 'inherit',
        fontFamily: 'inherit',
        border: 0,
        backgroundColor: 'transparent',
        padding: 0,
        textAlign: 'left',
      }}
      {...props}
    />
  );
}
